import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, afterNextRender, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule, CardModule, AlertModule, SpinnerModule, BadgeModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule, TranslateService } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { OrderService } from '../../../../../../services/order.service';
import { OrderWizardTracking, confirmationErrorCategory } from '../../../order-wizard-tracking';
// `@stripe/stripe-js` (main entry) injects js.stripe.com/v3 as a SIDE EFFECT of
// being imported, so every app that bundles the order wizard loaded Stripe.js —
// and got the `__stripe_mid` / `__stripe_sid` cookies — on every page, even
// though credit_card is not an enabled payment method anywhere (2026-10-10).
// The `/pure` entry only loads the script when loadStripe() is actually called,
// i.e. only on the Stripe confirmation step itself.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'app-stripe-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    AlertModule,
    SpinnerModule,
    BadgeModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './stripe-confirmation.component.html',
  styleUrls: ['./stripe-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StripeConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() paymentCosts: number = 0;
  @Input() totalWithCosts: number = 0;
  @Input() currency: string = 'USDT';
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();

  protected logger = new Logger('StripeConfirmationComponent');
  private orderService = inject(OrderService);
  private translate = inject(TranslateService);
  private tracking = inject(OrderWizardTracking, { optional: true });

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  initializing = signal<boolean>(true);
  loading = signal<boolean>(false);
  processing = signal<boolean>(false);
  errorMessage = signal<string>('');
  clientSecret = signal<string>('');
  publishableKey = signal<string>('');
  stripeMode = signal<string>('');

  // Stripe objects (not signals — managed imperatively)
  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardElement: StripeCardElement | null = null;

  // Computed
  canPay = computed(() => 
    !!this.clientSecret() && 
    !!this.stripe && 
    !this.processing() && 
    !this.errorMessage()
  );

  formattedTotal = computed(() => 
    this.totalWithCosts.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  );

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Auto-initialize after view render
    afterNextRender(async () => {
      if (!this.orderId) {
        this.errorMessage.set('Order ID is missing');
        this.initializing.set(false);
        return;
      }

      await this.initializeStripePayment();
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  private async initializeStripePayment(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set('');

    try {
      // Create PaymentIntent on backend
      const response = await lastValueFrom(this.orderService.initiatePayment(this.orderId!, 'credit_card'));

      this.clientSecret.set(response.stripe_client_secret || '');
      this.publishableKey.set(response.publishable_key || '');
      this.stripeMode.set(response.stripe_mode || 'sandbox');

      if (!this.clientSecret() || !this.publishableKey()) {
        throw new Error('Invalid payment configuration from server');
      }

      // Load Stripe
      this.stripe = await loadStripe(this.publishableKey()!);
      if (!this.stripe) {
        throw new Error('Failed to load Stripe');
      }

      this.initializing.set(false);

      // Mount card element (DOM must be ready)
      afterNextRender(() => {
        this.mountCardElement();
      });
    } catch (err: any) {
      this.logger.error('Stripe init error:', err);
      this.errorMessage.set(err?.error?.error || 'Failed to initialize payment. Please try again.');
      this.tracking?.step('payment_confirmation', 'error', confirmationErrorCategory(err));
    } finally {
      this.loading.set(false);
    }
  }

  private mountCardElement(): void {
    if (!this.stripe || !this.clientSecret()) return;

    try {
      this.elements = this.stripe.elements({ clientSecret: this.clientSecret()! });

      this.cardElement = this.elements.create('card', {
        style: {
          base: {
            fontSize: '16px',
            color: '#ffffff',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            '::placeholder': { color: 'rgba(255, 255, 255, 0.5)' }
          },
          invalid: { color: '#e5424d', iconColor: '#e5424d' }
        },
        hidePostalCode: false
      });

      const container = document.getElementById('card-element');
      if (container) {
        this.cardElement.mount(container);

        // Listen for changes/errors
        this.cardElement.on('change', (event) => {
          this.errorMessage.set(event.error?.message || '');
        });
      }
    } catch (err) {
      this.logger.error('Card mount error:', err);
      this.errorMessage.set('Failed to load payment form. Please refresh.');
    }
  }

  async confirmPayment(): Promise<void> {
    if (!this.stripe || !this.cardElement || !this.clientSecret() || !this.orderId) {
      this.errorMessage.set('Payment system not ready. Please refresh.');
      this.tracking?.step('payment_confirmation', 'error', 'other');
      return;
    }

    this.tracking?.step('payment_confirmation', 'submit');

    this.processing.set(true);
    this.errorMessage.set('');

    try {
      const { error, paymentIntent } = await this.stripe.confirmCardPayment(
        this.clientSecret()!,
        { payment_method: { card: this.cardElement } }
      );

      if (error) {
        // Stripe: card_error = declined by the issuer, validation_error = bad card input.
        this.tracking?.step('payment_confirmation', 'error',
          error.type === 'card_error' ? 'payment_declined' : error.type === 'validation_error' ? 'validation' : 'other');
        this.errorMessage.set(error.message || 'Payment failed. Please try again.');
        this.processing.set(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        try {
          const confirmResponse = await this.orderService.confirmStripePayment(
            this.orderId,
            paymentIntent.id
          ).toPromise();

          this.onSuccess.emit({
            orderId: this.orderId,
            isFirstOrder: confirmResponse?.is_first_order || false,
            dateOfJoining: confirmResponse?.date_of_joining
          });
        } catch (confirmErr: any) {
          // Webhook should catch this — still emit success
          this.logger.warn('Backend confirmation failed (webhook will handle):', confirmErr);
          this.onSuccess.emit({ orderId: this.orderId, isFirstOrder: false });
        }
      } else {
        this.errorMessage.set('Payment is being processed. Check your order status.');
      }
    } catch (err: any) {
      this.logger.error('Payment confirm error:', err);
      this.errorMessage.set('Unexpected error. Please try again.');
      this.tracking?.step('payment_confirmation', 'error', confirmationErrorCategory(err));
    } finally {
      this.processing.set(false);
    }
  }

  goBack(): void {
    this.onBack.emit();
  }

  async retry(): Promise<void> {
    this.errorMessage.set('');
    this.initializing.set(true);
    await this.initializeStripePayment();

    if (this.stripe && this.clientSecret()) {
      afterNextRender(() => this.mountCardElement());
    }
  }
}